import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './Icon';
import GlobalSearch from './GlobalSearch';
import { useRole } from '../hooks/useRole';
import { AccountHeaderSlotsContext } from './account/accountHeaderSlots';
import DemoBadge from './DemoBadge';
import GlobalRefreshButton from './header/GlobalRefreshButton';
import { runPageRefresh, usePageRefreshState } from '../hooks/usePageRefresh';
import { GUARD_SAFE_ATTR } from '../hooks/useUnsavedChangesGuard';
import { findAccountNavItem, visibleAccountNav, type AccountNavItem } from './account/accountNav';
import '../styles/components/Sidebar.css';
import '../styles/components/Header.css';
import '../styles/components/AccountShell.css';

interface AccountShellProps {
  activeView: string;
  children: React.ReactNode;
}

/**
 * The workspace-level shell: its own sidebar (see accountNav.ts) and the same
 * two-tier header the per-project screens have. The top bar carries the page
 * icon and title; the bar under it holds whatever the page declares through
 * <AccountBar> (New button, filters, Archived toggle, Save/Cancel).
 */
const AccountShell: React.FC<AccountShellProps> = ({ activeView, children }) => {
  const navigate = useNavigate();
  const { isOwner, isAdmin } = useRole();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [crumbSlot, setCrumbSlot] = useState<HTMLElement | null>(null);
  const [actionSlot, setActionSlot] = useState<HTMLElement | null>(null);
  const [barCount, setBarCount] = useState(0);
  const refresh = usePageRefreshState();

  const registerBar = useCallback(() => {
    setBarCount(n => n + 1);
    return () => setBarCount(n => Math.max(0, n - 1));
  }, []);
  const slots = useMemo(
    () => ({ crumbs: crumbSlot, actions: actionSlot, registerBar }),
    [crumbSlot, actionSlot, registerBar],
  );

  const sections = useMemo(() => visibleAccountNav({ isOwner, isAdmin }), [isOwner, isAdmin]);
  const current = findAccountNavItem(activeView);

  const open = (item: AccountNavItem) => {
    if (item.comingSoon) return;
    setMobileOpen(false);
    navigate(item.path);
  };

  return (
    <div className="App">
      <aside className={`sidebar${mobileOpen ? ' sidebar--mobile-open' : ''}`} aria-label="Workspace navigation">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="logo-icon">
              <Icon name="ring" size={24} />
            </div>
            <span className="logo-text">ĀNƆ̄ JĒJĒ</span>
          </div>
        </div>
        <nav className="sidebar-nav" aria-label="Workspace">
          {sections.map(section => (
            <div className="nav-section" key={section.title} data-section={section.title}>
              <div className="nav-section-title as-cursor-default">
                <span>{section.title}</span>
                <span className="nav-section-title-rule" />
              </div>
              <div className="nav-items">
                {section.items.map(item => (
                  <button
                    key={item.id}
                    type="button"
                    className={`nav-item ${activeView === item.id ? 'active' : ''} ${item.comingSoon ? 'disabled' : ''}`}
                    onClick={() => open(item)}
                    disabled={item.comingSoon}
                    aria-current={activeView === item.id ? 'page' : undefined}
                    title={item.comingSoon ? 'Coming soon' : item.label}
                  >
                    <div className="nav-item-icon">
                      <Icon name={item.icon} size={18} />
                    </div>
                    <span className="nav-item-label">{item.label}</span>
                    {item.comingSoon && <span className="as-soon-badge">Soon</span>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>
      <div className="layout-right">
        <header className="global-header">
          <div className="global-header-left">
            <button
              type="button"
              className="global-header-hamburger"
              onClick={() => setMobileOpen(v => !v)}
              aria-label="Open navigation menu"
              aria-expanded={mobileOpen}
              {...{ [GUARD_SAFE_ATTR]: '' }}
            >
              <span /><span /><span />
            </button>
            {current && (
              <div className="global-header-page-icon">
                <Icon name={current.icon} size={20} />
              </div>
            )}
            <h1 className="global-header-title">{current?.label ?? ''}</h1>
          </div>
          <div className="global-header-right">
            <DemoBadge />
            <GlobalSearch />
            {refresh.available && <GlobalRefreshButton onRefresh={() => { void runPageRefresh(); }} busy={refresh.busy} />}
          </div>
        </header>
        <div className="global-breadcrumb-bar as-bar" hidden={barCount === 0}>
          <div className="global-breadcrumb-left" ref={setCrumbSlot} />
          <div className="as-bar-actions" ref={setActionSlot} />
        </div>
        <AccountHeaderSlotsContext.Provider value={slots}>
          <main className="main-content">
            {children}
          </main>
        </AccountHeaderSlotsContext.Provider>
      </div>
      {mobileOpen && (
        <div className="mobile-sidebar-backdrop" onClick={() => setMobileOpen(false)} aria-hidden="true" />
      )}
    </div>
  );
};

export default AccountShell;
